import { Injectable } from '@angular/core';
import {
    AbstractControl,
    FormControl,
    FormGroup,
    ValidatorFn,
} from '@angular/forms';
import {
    DatiqInputService,
    IDatiqOption,
} from '@dat/datiq-components-pkg/utils';
import {
    DATIQ_DEFAULT_ARRAY,
    DATIQ_DEFAULT_STRING,
} from '@dat/datiq-core/models';
import { isEmpty, uniqBy } from 'lodash';
import {
    DatiqSelectTypesInput,
    DatiqSelectTypesSelect,
    IDatiqSelect,
} from '../models';

@Injectable({
    providedIn: 'root',
})
export class DatiqSelectService {
    constructor(private _inputService: DatiqInputService) {}

    createSelectControl(
        settings: IDatiqSelect,
        fg: FormGroup
    ): AbstractControl {
        const formControl: FormControl = new FormControl();
        const validators: ValidatorFn[] = [
            ...this._inputService.createControlValidation(settings, fg),
        ];
        const selectionValue: DatiqSelectTypesSelect =
            this.getSyncedSelectValue(
                settings?.value as DatiqSelectTypesInput,
                settings
            );

        formControl.setValidators(validators);
        formControl.setValue(selectionValue);

        settings.disabled ? formControl.disable() : formControl.enable();

        return formControl;
    }

    addValidatorsToControl(
        settings: IDatiqSelect,
        control: AbstractControl,
        fg: FormGroup
    ): void {
        const validators: ValidatorFn[] = [
            ...this._inputService.createControlValidation(settings),
        ];
        control.setValidators(validators);
        control.updateValueAndValidity();
    }

    getMultipleSelectedValues(options: IDatiqOption[]): string[] {
        return !!options?.length
            ? options.map((option: IDatiqOption): string => option.value)
            : [];
    }

    updateCheckedValues(
        shouldAdd: boolean,
        option: IDatiqOption,
        settings: IDatiqSelect
    ): IDatiqSelect {
        return !!shouldAdd
            ? this.addOptionToCurrentValues(option, settings)
            : this.removeOptionFromCurrentValues(option, settings);
    }

    addOptionToCurrentValues(
        option: IDatiqOption,
        settings: IDatiqSelect
    ): IDatiqSelect {
        const oldValue: IDatiqOption[] = Array.isArray(settings?.value)
            ? settings?.value
            : [];

        const currentValues: IDatiqOption[] = uniqBy(
            [...oldValue, option],
            'value'
        );

        return {
            ...settings,
            value: currentValues,
        };
    }

    removeOptionFromCurrentValues(
        optionItem: IDatiqOption,
        settings: IDatiqSelect
    ): IDatiqSelect {
        const oldValue: IDatiqOption[] = Array.isArray(settings?.value)
            ? settings?.value
            : [];

        const currentValues: IDatiqOption[] = oldValue.filter(
            (option: IDatiqOption): boolean =>
                !this.optionMatchesValue(option, optionItem.value)
        );

        return {
            ...settings,
            value: currentValues,
        };
    }

    optionMatchesValue(option: IDatiqOption, value: string): boolean {
        return !!option?.value && !!value?.length && option.value === value;
    }

    getSelectedValuesFromSettings(settings: IDatiqSelect): string[] {
        const options: IDatiqOption[] = settings.value as IDatiqOption[];

        return !!options?.length && this.valuesIsInItems(options, settings)
            ? this.getValuesFromOptions(options)
            : (DATIQ_DEFAULT_ARRAY as string[]);
    }

    getValuesFromOptions(options: IDatiqOption[]): string[] {
        return options.map((option: IDatiqOption): string => option?.value);
    }

    valuesIsInItems(options: IDatiqOption[], settings: IDatiqSelect): boolean {
        return (
            !!settings?.options?.length &&
            settings?.options?.some((item: IDatiqOption): boolean =>
                this.optionIsInOptions(item, options)
            )
        );
    }

    optionIsInOptions(
        optionItem: IDatiqOption,
        options: IDatiqOption[]
    ): boolean {
        return (
            !!options?.length &&
            options.some((option: IDatiqOption): boolean =>
                this.optionMatchesValue(option, optionItem.value)
            )
        );
    }

    updateMultiValue(values: string[], settings: IDatiqSelect): IDatiqSelect {
        const value: IDatiqOption[] = !!values?.length
            ? settings?.options.filter((option: IDatiqOption): boolean =>
                  this.optionIsInValues(option, values)
              )
            : [];

        return {
            ...settings,
            value,
        };
    }

    optionIsInValues(option: IDatiqOption, values: string[]): boolean {
        return values.indexOf(option.value) >= 0;
    }

    updateCheckedValueFromValue(
        value: string,

        settings: IDatiqSelect
    ): IDatiqSelect {
        const checkedOption: IDatiqOption = this.getOptionFromSettings(
            value,
            settings
        );

        return {
            ...settings,
            value: checkedOption,
        };
    }

    getOptionFromSettings(value: string, settings: IDatiqSelect): IDatiqOption {
        return (
            settings?.options?.find((option: IDatiqOption): boolean =>
                this.optionMatchesValue(option, value)
            ) || { value: '', display: '' }
        );
    }

    getSelectedValueFromSettings(settings: IDatiqSelect): string {
        const option: IDatiqOption = settings.value as IDatiqOption;

        return !!option?.value && this.valueIsInOptions(option?.value, settings)
            ? option?.value
            : DATIQ_DEFAULT_STRING;
    }

    valueIsInOptions(value: string, settings: IDatiqSelect): boolean {
        return (
            !!settings?.options?.length &&
            settings?.options?.some((item: IDatiqOption): boolean =>
                this.optionMatchesValue(item, value)
            )
        );
    }

    isSelectAndInputOutOfSync(
        selectValue: DatiqSelectTypesSelect,
        inputValue: DatiqSelectTypesInput,
        settings: IDatiqSelect
    ): boolean {
        const inputOutOfSyncSelect: boolean = this.isInputOutOfSyncWithSelect(
            selectValue,
            inputValue,
            settings
        );

        return inputOutOfSyncSelect;
    }

    getSyncedInputValue(
        selectValue: DatiqSelectTypesSelect,
        settings: IDatiqSelect
    ): DatiqSelectTypesInput {
        const isArray: boolean = Array.isArray(selectValue);
        const hasSelectValue: boolean = this.hasSelectValue(selectValue);

        return !hasSelectValue
            ? null
            : !isArray
            ? this.getOptionFromValue(selectValue as string, settings.options)
            : isArray
            ? this.getOptionsFromValues(
                  selectValue as string[],
                  settings.options
              )
            : null;
    }

    getOptionFromValue(
        value: string,
        options: IDatiqOption[]
    ): DatiqSelectTypesInput {
        return options.find((option: IDatiqOption): boolean =>
            this.optionMatchesValue(option, value)
        );
    }

    getOptionsFromValues(
        values: string[],
        options: IDatiqOption[]
    ): DatiqSelectTypesInput {
        return options.filter((option: IDatiqOption) =>
            values.some((selectValue: string): boolean =>
                this.optionMatchesValue(option, selectValue)
            )
        );
    }

    getSyncedSelectValue(
        inputValue: DatiqSelectTypesInput,
        settings: IDatiqSelect
    ): DatiqSelectTypesSelect {
        const isArray: boolean = Array.isArray(inputValue);
        const hasInputValue: boolean = this.hasInputValue(inputValue);

        return !hasInputValue
            ? null
            : !isArray
            ? (inputValue as IDatiqOption).value
            : isArray
            ? (inputValue as IDatiqOption[]).map(
                  (option: IDatiqOption): string => option.value
              )
            : null;
    }

    isInputOutOfSyncWithSelect(
        selectValue: DatiqSelectTypesSelect,
        inputValue: DatiqSelectTypesInput,
        settings: IDatiqSelect
    ): boolean {
        const isOptionSame: boolean = this.isOptionSame(
            selectValue,
            inputValue
        );
        const areOptionsSame: boolean = this.areOptionsSame(
            selectValue,
            inputValue
        );
        const bothHaveNoValue: boolean = this.bothHaveNoValue(
            selectValue,
            inputValue
        );

        return (
            !bothHaveNoValue &&
            (settings.multiple ? !isOptionSame : !areOptionsSame)
        );
    }

    bothHaveNoValue(
        selectValue: DatiqSelectTypesSelect,
        inputValue: DatiqSelectTypesInput
    ): boolean {
        return (
            !this.hasSelectValue(selectValue) && !this.hasInputValue(inputValue)
        );
    }

    private areOptionsSame(
        selectValue: DatiqSelectTypesSelect,
        inputValue: DatiqSelectTypesInput
    ): boolean {
        const hasSelectValue: boolean = this.hasSelectValue(selectValue);
        const hasInputValue: boolean = this.hasInputValue(inputValue);
        const bothAreArrays: boolean = this.bothAreArrays(
            selectValue,
            inputValue
        );

        return (
            hasSelectValue &&
            hasInputValue &&
            !bothAreArrays &&
            (selectValue as string) === (inputValue as IDatiqOption).value
        );
    }

    private hasInputValue(inputValue: DatiqSelectTypesInput): boolean {
        return !!inputValue && !isEmpty(inputValue);
    }

    private hasSelectValue(selectValue: DatiqSelectTypesSelect): boolean {
        return !!selectValue && !!selectValue?.length;
    }

    private isOptionSame(
        selectValue: DatiqSelectTypesSelect,
        inputValue: DatiqSelectTypesInput
    ): boolean {
        const hasSelectValue: boolean = this.hasSelectValue(selectValue);
        const hasInputValue: boolean = this.hasInputValue(inputValue);
        const bothAreArrays: boolean = this.bothAreArrays(
            selectValue,
            inputValue
        );
        const selectValuesMatchInputOptions: boolean =
            this.doSelectValuesMatchInputOptions(selectValue, inputValue);

        return (
            hasSelectValue &&
            hasInputValue &&
            bothAreArrays &&
            selectValuesMatchInputOptions
        );
    }

    doSelectValuesMatchInputOptions(
        selectValue: DatiqSelectTypesSelect,
        inputValue: DatiqSelectTypesInput
    ): boolean {
        const everySelectMatchesInput: boolean = this.everySelectMatchesInput(
            selectValue,
            inputValue
        );
        const everyInputMatchesSelect: boolean = this.everyInputMatchesSelect(
            inputValue,
            selectValue
        );

        return everySelectMatchesInput && everyInputMatchesSelect;
    }

    private everyInputMatchesSelect(
        inputValue: DatiqSelectTypesInput,
        selectValue: DatiqSelectTypesSelect
    ): boolean {
        const bothAreArrays = this.bothAreArrays(selectValue, inputValue);
        return (
            bothAreArrays &&
            (inputValue as IDatiqOption[]).every(
                (currentInputValue: IDatiqOption): boolean =>
                    (selectValue as string[]).some(
                        (currentSelectValue: string): boolean =>
                            currentInputValue.value === currentSelectValue
                    )
            )
        );
    }

    private everySelectMatchesInput(
        selectValue: DatiqSelectTypesSelect,
        inputValue: DatiqSelectTypesInput
    ): boolean {
        const bothAreArrays = this.bothAreArrays(selectValue, inputValue);
        return (
            bothAreArrays &&
            (selectValue as string[]).every(
                (currentSelectValue: string): boolean =>
                    (inputValue as IDatiqOption[]).some(
                        (currentInputValue: IDatiqOption): boolean =>
                            currentInputValue.value === currentSelectValue
                    )
            )
        );
    }

    private bothAreArrays(
        selectValue: DatiqSelectTypesSelect,
        inputValue: DatiqSelectTypesInput
    ): boolean {
        return Array.isArray(selectValue) && Array.isArray(inputValue);
    }
}
